import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '';

interface ThumbnailPickerProps {
  shortId: string;
  thumbnailFrames: string[];
  currentTime: number;
  currentThumbnail?: string;
  onSelect: (thumbnailUrl: string) => void;
  onClose: () => void;
}

const ThumbnailPicker: React.FC<ThumbnailPickerProps> = ({
  shortId, thumbnailFrames, currentTime, currentThumbnail, onSelect, onClose,
}) => {
  const [tab, setTab] = useState<'frames' | 'upload'>('frames');
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFrameSelect = (frameUrl: string) => {
    setSelectedFrame(frameUrl);
  };

  const handleUseSelected = async () => {
    if (!selectedFrame) return;
    // Extract timestamp from the frameUrl
    const match = selectedFrame.match(/frame\/([\d.]+)/);
    if (match) {
      const res = await fetch(`${API_BASE}/api/shorts/${shortId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frameTimestamp: parseFloat(match[1]) }),
      });
      const data = await res.json();
      // The key is already stored server-side; what comes back here is only for display.
      if (res.ok) onSelect(data.src || data.thumbnailUrl);
    }
  };

  const handleCurrentFrame = () => {
    const url = `${API_BASE}/api/shorts/${shortId}/frame/${currentTime.toFixed(1)}`;
    setSelectedFrame(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('File must be under 2MB');
      return;
    }

    // Preview
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/api/shorts/${shortId}/thumbnail`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json();
    setUploading(false);
    if (res.ok) onSelect(data.src || data.thumbnailUrl);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="thumbnail-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h3>Thumbnail</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="thumbnail-tabs">
          <button className={`tab-btn ${tab === 'frames' ? 'active' : ''}`} onClick={() => setTab('frames')}>
            Frames
          </button>
          <button className={`tab-btn ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>
            Upload
          </button>
        </div>

        {tab === 'frames' && (
          <div className="thumbnail-frames-tab">
            <div className="thumbnail-frame-grid">
              {thumbnailFrames.map((frameUrl, i) => (
                <div
                  key={i}
                  className={`thumbnail-frame-card ${selectedFrame === frameUrl ? 'selected' : ''}`}
                  onClick={() => handleFrameSelect(frameUrl)}
                >
                  <img
                    src={`${API_BASE}${frameUrl}`}
                    alt={`Frame ${i + 1}`}
                    crossOrigin="use-credentials"
                  />
                  <span className="frame-time">{frameUrl.match(/frame\/([\d.]+)/)?.[1]}s</span>
                </div>
              ))}
            </div>
            <div className="thumbnail-frame-actions">
              <button className="btn-sm" onClick={handleCurrentFrame}>Current Frame</button>
              <button
                className="btn-sm btn-accent"
                disabled={!selectedFrame}
                onClick={handleUseSelected}
              >
                Use Selected
              </button>
            </div>
          </div>
        )}

        {tab === 'upload' && (
          <div className="thumbnail-upload-tab">
            {preview ? (
              <div className="thumbnail-upload-preview">
                <img src={preview} alt="Preview" />
                {uploading && <div className="upload-spinner">Uploading...</div>}
              </div>
            ) : (
              <label className="thumbnail-dropzone">
                <span>📁 Click to upload</span>
                <span className="dropzone-hint">PNG, JPEG, WebP · 2MB max</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleFileUpload}
                  hidden
                />
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThumbnailPicker;
